import {
  isSpecimenInformationMissing,
  SPECIMEN_INFORMATION_MISSING_MESSAGE,
} from '@ldt/features/orders/components/specimen_information_form_section';
import { hasLdtValidationErrors } from '@ldt/features/orders/utils/has_ldt_validation_errors';
import type {
  CheckOrderValidationErrorsContext,
  GetAvailableActionsContext,
  LdtOrderAction,
} from '@ldt/workflows/types';
import { LdtOrderAction as Action } from '@ldt/workflows/types';
import { LdtOrderDetailFieldsFragment, OrderStatus } from 'src/types';
import { WorkflowType } from 'src/types/workflows';
import {
  isClinicAdmin,
  isCustomerSuccess,
  isCustomerSuccessOrSalesUser,
  isOrderingPhysician,
  isSalesUser,
} from 'src/utils';

function getQcPendingActions(
  missingInfo: string[],
  hasValidationErrors: boolean,
): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  // For ordering-physician and clinic-admin users
  if (isOrderingPhysician() || isClinicAdmin()) {
    actions.push(Action.UpdateFiles);
  }
  // For CS and Sales users
  else if (isCustomerSuccessOrSalesUser()) {
    // Only show QC Approve if there are no missing information messages
    if (!hasValidationErrors && missingInfo.length === 0)
      actions.push(Action.ApproveQC);
    actions.push(Action.EditOrder, Action.Cancel);
  }
  return actions;
}

function getReportReadyActions(): LdtOrderAction[] {
  const actions: LdtOrderAction[] = [];
  if (isOrderingPhysician() || isClinicAdmin()) {
    actions.push(Action.DisplayPatientReport);
  } else if (isCustomerSuccess()) {
    actions.push(
      Action.DisplayPatientReport,
      Action.DisplayDeIdentifiedReport,
      Action.ReviseReportMutation,
    );
  } else if (isSalesUser()) {
    actions.push(Action.DisplayDeIdentifiedReport);
  }
  return actions;
}

export function getAvailableActions(
  ctx: GetAvailableActionsContext,
): LdtOrderAction[] {
  const { order, missingInformationMessages, hasValidationErrors } = ctx;
  const status = order.status;

  if (status === OrderStatus.Draft) return [];
  if (status === OrderStatus.QualityControlPending) {
    return getQcPendingActions(missingInformationMessages, hasValidationErrors);
  }
  if (status === OrderStatus.QualityControlApproved) return [];
  if (
    status === OrderStatus.ReportReady ||
    status === OrderStatus.ReportFaxed
  ) {
    return getReportReadyActions();
  }
  return [];
}

export function getOrderMissingInfoMessages(
  order: LdtOrderDetailFieldsFragment,
): string[] {
  const messages: string[] = [];
  if (isCustomerSuccessOrSalesUser() && isSpecimenInformationMissing(order)) {
    messages.push(SPECIMEN_INFORMATION_MISSING_MESSAGE);
  }
  return messages;
}

export async function checkOrderValidationErrors(
  context: CheckOrderValidationErrorsContext,
): Promise<boolean> {
  return hasLdtValidationErrors(
    context.order,
    isCustomerSuccessOrSalesUser(),
    WorkflowType.ANCILLARY_LAB,
    context.order.status === OrderStatus.QualityControlPending,
    context.order.specimenType,
  );
}
